import { useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, FileWarning } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const DISPUTE_REASONS = [
  { value: 'not_delivered', label: 'Order Not Delivered', description: 'The dubplate was never delivered' },
  { value: 'quality_issue', label: 'Quality Issue', description: 'The audio quality is poor or unusable' },
  { value: 'wrong_content', label: 'Wrong Content', description: 'The delivered content is not what was requested' },
  { value: 'unresponsive', label: 'Unresponsive Party', description: 'The other party is not responding' },
  { value: 'other', label: 'Other Issue', description: 'Another issue not listed above' },
];

export default function DisputeDialog({ open, onOpenChange, order, onDisputeCreated }) {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason for the dispute');
      return;
    }
    if (description.length < 10) {
      toast.error('Please provide more details (at least 10 characters)');
      return;
    }

    setSubmitting(true);
    try {
      await api.createDispute(order.id, reason, description);
      toast.success('Dispute submitted successfully. Our team will review it shortly.');
      onOpenChange(false);
      setReason('');
      setDescription('');
      if (onDisputeCreated) onDisputeCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedReason = DISPUTE_REASONS.find(r => r.value === reason);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-[#262626] max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            Report an Issue
          </DialogTitle>
          <DialogDescription>
            Submit a dispute for order with {order?.artist_name || order?.user_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Order Info */}
          <div className="bg-[#1a1a1a] rounded-lg p-3 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-[#888]">Order ID</span>
              <span className="font-mono">{order?.id?.slice(0, 8)}...</span>
            </div>
            <div className="flex justify-between mb-1">
              <span className="text-[#888]">Amount</span>
              <span className="font-mono">${order?.price?.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#888]">Status</span>
              <span className="capitalize">{order?.status}</span>
            </div>
          </div>

          {/* Reason Select */}
          <div>
            <Label htmlFor="dispute-reason">Reason for Dispute</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="input-dark mt-1" data-testid="dispute-reason-select">
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent className="bg-surface border-[#262626]">
                {DISPUTE_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value} className="focus:bg-surface-highlight">
                    <div className="flex items-center gap-2">
                      <FileWarning className="w-4 h-4 text-red-400" />
                      {r.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReason && (
              <p className="text-xs text-[#888] mt-1">{selectedReason.description}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="dispute-description">Describe the Issue</Label>
            <Textarea
              id="dispute-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide details about the issue. Include any relevant information that will help us resolve this dispute..."
              className="input-dark mt-1 min-h-[120px]"
              maxLength={2000}
              data-testid="dispute-description"
            />
            <p className="text-xs text-[#666] mt-1 text-right">{description.length}/2000</p>
          </div>

          {/* Warning */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-sm">
            <p className="text-yellow-400 font-medium mb-1">Important</p>
            <ul className="text-[#A1A1AA] text-xs space-y-1">
              <li>• Disputes must be raised within 7 days of delivery</li>
              <li>• Fund release will be paused until the dispute is resolved</li>
              <li>• Our team will review your case within 24-48 hours</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !reason || description.length < 10}
            className="bg-red-500 hover:bg-red-600 text-white"
            data-testid="submit-dispute-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Submit Dispute
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
